import { useLazyQuery, useMutation } from '@apollo/client';
import {
  ActionTarget,
  IEnhancedMenuItem,
  IListingTableFilter,
} from '@components/table/types';
import ListingTableWithPagination from '@components/table/with-pagination/ListingTableWithPagination';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { OpenConfirmationAction } from '@redux/confirmation/actions';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { getError } from '@utils/graph-utils';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import SalesforceAssignedBPPlanDrawer from './salesforce-assign-business-partner-plan/SalesforceAssignedBPPlanDrawer';
import {
  activateAssignedBPPlan,
  deactivateAssignedBPPlan,
  getAssignedBPLineListWithoutPagination,
  getAssignedPlanListWithoutPagination,
  getBusinessPartnersPlansList,
} from './queries';
import { useParams } from 'react-router-dom';
import {
  extractAssignedBP,
  mapToBusinessPartnersPlansListingData,
} from './utils';
import { IListingData } from '@models/listing';
import { IAbstractRecord } from '@models';
import { businessPartnersPlansHeaders, filterSectionsContent } from './content';
import { IConfirmation } from '@redux/confirmation/types';
import { useAppDispatch } from '@redux/hooks';
import { useTabsLayoutStyles } from '@styles/sharedStyles';
import { capitalizeFirstCharacter } from '@utils/formatting-utils';

interface IBusinessPartnersAndPlans {
  businessUserStatus: string;
}

const BusinessPartnersAndPlansTab: React.FC<IBusinessPartnersAndPlans> = ({
  businessUserStatus,
}) => {
  const { classes } = useTabsLayoutStyles();
  const { id: entityId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const [loadingState, setLoadingState] = useState<boolean>(false);
  const [businessPartnersPlansDrawerOpen, setBusinessPartnersPlansDrawerOpen] =
    useState<boolean>(false);

  const [businessPartnersPlansTableData, setBusinessPartnersPlansTableData] =
    useState<IListingData<any>>({
      pagedItems: {},
      pageSize: 10,
      pageNumber: 1,
      totalCount: 0,
    });

  const [lovs, setLovs] =
    useState<Record<string, Record<string, string>>>(null);
  const [filterSections, setFilterSections] = useState<any[]>([]);
  const [filterValues, setFilterValues] = useState<IAbstractRecord>({
    bp: [],
    line: [],
    plan: [],
  });

  const [fetchBusinessPartnersPlans, { loading }] = useLazyQuery(
    getBusinessPartnersPlansList,
    { errorPolicy: 'all' }
  );
  const [fetchAssignedBPLines] = useLazyQuery(
    getAssignedBPLineListWithoutPagination,
    { errorPolicy: 'all' }
  );
  const [fetchAssignedPlanList] = useLazyQuery(
    getAssignedPlanListWithoutPagination,
    { errorPolicy: 'all' }
  );
  const [activateAssignedBPPlanAction] = useMutation(activateAssignedBPPlan, {
    errorPolicy: 'all',
    refetchQueries: [getBusinessPartnersPlansList],
  });
  const [deactivateAssignedBPPlanAction] = useMutation(
    deactivateAssignedBPPlan,
    {
      errorPolicy: 'all',
      refetchQueries: [getBusinessPartnersPlansList],
    }
  );

  const getSelectedIds = (
    selectedNames: string[],
    mapping: Record<string, string>
  ): string[] => {
    return Object.entries(mapping)
      .filter(([id, name]) => selectedNames.includes(name))
      .map(([id]) => id);
  };

  const loadBusinessPartnersPlansList = async (
    currentPage: number,
    pageSize: number,
    searchKey: string,
    order: string,
    orderBy: string,
    filters?: Record<string, IListingTableFilter>
  ) => {
    // setLoadingState(true);
    const selectedBusinessPartnerIDs = filters?.person_FullName?.selectedValues
      ? getSelectedIds(filters.person_FullName.selectedValues, lovs.bp)
      : [];
    const selectedLineIDs = filters?.line_Name?.selectedValues
      ? getSelectedIds(filters.line_Name.selectedValues, lovs.line)
      : [];
    const selectedPlanIDs = filters?.plan_Name?.selectedValues
      ? getSelectedIds(filters.plan_Name.selectedValues, lovs.plan)
      : [];
    try {
      const result = await fetchBusinessPartnersPlans({
        variables: {
          currentPage,
          currentPageSize: pageSize,
          selectedBusinessUserID: entityId,
          selectedBusinessPartnerID: selectedBusinessPartnerIDs,
          selectedLineID: selectedLineIDs,
          selectedPlanID: selectedPlanIDs,
          Attribute: capitalizeFirstCharacter(
            orderBy || 'BusinessPartner_Code'
          ),
          Descending: order === 'desc',
          keywordSearch: searchKey || null,
        },
      });

      const mappedData = mapToBusinessPartnersPlansListingData(result?.data);
      setBusinessPartnersPlansTableData(mappedData);
    } catch (err) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    } finally {
      // setLoadingState(false);
    }
  };

  const loadAssignedBPAndPlanLOVs = async () => {
    const [bpLinesResult, planListResult] = await Promise.all([
      fetchAssignedBPLines({
        variables: { selectedBusinessUserID: entityId },
      }),
      fetchAssignedPlanList({
        variables: { selectedBusinessUserID: entityId, selectedLineID: [] },
      }),
    ]);
    const lovs = extractAssignedBP(bpLinesResult.data, planListResult.data);
    setLovs(lovs);
    return lovs;
  };

  const initialize = async () => {
    try {
      await loadBusinessPartnersPlansList(
        1,
        businessPartnersPlansTableData.pageSize,
        '',
        'asc',
        '',
        {}
      );
      const lovs = await loadAssignedBPAndPlanLOVs();
      const newFilterSections = filterSectionsContent(lovs, filterValues);
      setFilterSections(newFilterSections);
      setLovs(lovs);
    } catch (error) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  useEffect(() => {
    initialize();
  }, []);

  const handleBusinessPartnerPlanPageChange = async (
    page: number,
    search?: string,
    rowsPerPage?: number,
    order?: string,
    orderBy?: string,
    filters?: Record<string, IListingTableFilter>
  ) => {
    await loadBusinessPartnersPlansList(
      page,
      rowsPerPage ?? businessPartnersPlansTableData.pageSize,
      search ?? '',
      order ?? 'asc',
      orderBy ?? '',
      filters ?? {}
    );
  };

  const businessPartnersPlansListingActions: IEnhancedMenuItem[] = [
    {
      name: 'assignBPPlan',
      title: 'Assign Business Partner and Plan',
      onClick: () => setBusinessPartnersPlansDrawerOpen(true),
      rowIndependant: false,
      disabled: businessUserStatus === 'inactive',
    },
    {
      name: 'activatePlan',
      title: 'Activate Plan(s)',
      onClick: (selectedRowIds) => {
        if (!selectedRowIds || selectedRowIds.length === 0) return;
        const confirmation: IConfirmation = {
          open: true,
          title: 'Activate',
          message: 'Are you sure you want to activate the selected plan(s)?',
          callback: async () => {
            try {
              const result = await activateAssignedBPPlanAction({
                variables: {
                  businessUserID: entityId,
                  selectedAssignedBPPlanIds: selectedRowIds,
                },
              });
              if (!result.errors) {
                toast.success(
                  <ToastSuccessMessage>
                    Assigned plan(s) successfully activated.
                  </ToastSuccessMessage>
                );
              } else {
                toast.error(
                  <ToastErrorMessage>{getError(result)}</ToastErrorMessage>
                );
              }
            } catch (error) {
              toast.error(
                <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
              );
            }
          },
          submitButtonText: 'Submit',
          cancelButtonText: 'Cancel',
        };
        dispatch(OpenConfirmationAction(confirmation));
      },
      target: ActionTarget.Entity,
      rowIndependant: false,
    },
    {
      name: 'deactivatePlan',
      title: 'Deactivate Plan(s)',
      onClick: (selectedRowIds) => {
        if (!selectedRowIds || selectedRowIds.length === 0) return;
        const confirmation: IConfirmation = {
          open: true,
          title: 'Deactivate',
          message: 'Are you sure you want to deactivate the selected plan(s)?',
          callback: async () => {
            try {
              const result = await deactivateAssignedBPPlanAction({
                variables: {
                  businessUserID: entityId,
                  selectedAssignedBPPlanIds: selectedRowIds,
                },
              });
              if (!result.errors) {
                toast.success(
                  <ToastSuccessMessage>
                    Assigned plan(s) successfully deactivated.
                  </ToastSuccessMessage>
                );
              } else {
                toast.error(
                  <ToastErrorMessage>{getError(result)}</ToastErrorMessage>
                );
              }
            } catch (error) {
              toast.error(
                <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
              );
            }
          },
          submitButtonText: 'Submit',
          cancelButtonText: 'Cancel',
        };
        dispatch(OpenConfirmationAction(confirmation));
      },
      target: ActionTarget.Entity,
      rowIndependant: false,
    },
  ];

  return (
    <section className={classes.tabContents}>
      <ListingTableWithPagination
        inlineTitle="Business Partners and Plans"
        name="AssignedBPPlan"
        data={businessPartnersPlansTableData}
        headers={businessPartnersPlansHeaders}
        actions={businessPartnersPlansListingActions}
        dropdownActionsNames={[
          'assignBPPlan',
          'activatePlan',
          'deactivatePlan',
        ]}
        loader={loading}
        orderByAscendingByDefault
        defaultOrderByColumn="BusinessPartner_Code"
        inline
        onPageChange={handleBusinessPartnerPlanPageChange}
        filterableNames={['person_FullName', 'line_Name', 'plan_Name']}
      />
      {businessPartnersPlansDrawerOpen && (
        <SalesforceAssignedBPPlanDrawer
          open={businessPartnersPlansDrawerOpen}
          onClose={() => setBusinessPartnersPlansDrawerOpen(false)}
          businessUserId={entityId}
          onSuccess={() => {
            handleBusinessPartnerPlanPageChange(1);
          }}
        />
      )}
    </section>
  );
};

export default BusinessPartnersAndPlansTab;
